// Solar calculation utilities

/**
 * Calculate required KW based on monthly bill and state electricity rate
 * @param {number} monthlyBill - Monthly electricity bill in rupees
 * @param {number} electricityRate - Electricity rate per unit in rupees
 * @returns {number} Required KW capacity (rounded up to nearest standard size)
 */
export const calculateRequiredKW = (monthlyBill, electricityRate) => {
  if (!monthlyBill || !electricityRate || electricityRate <= 0) {
    return null;
  }

  // Calculate monthly units consumed
  const monthlyUnits = monthlyBill / electricityRate;
  
  // Calculate yearly units (assuming consistent consumption)
  const yearlyUnits = monthlyUnits * 12;
  
  // Calculate required KW (1400 units per KW per year)
  const requiredKW = yearlyUnits / 1400;
  
  // Standard sizes available
  const standardSizes = [2, 3, 4, 5, 10];
  
  // Find the smallest standard size that meets at least 80% of requirement
  // This prevents oversizing and keeps costs manageable
  const targetKW = requiredKW * 0.8;
  
  for (const size of standardSizes) {
    if (size >= targetKW) {
      return size;
    }
  }
  
  // If requirement exceeds 10KW, return 10KW (largest standard size)
  return 10;
};

/**
 * Calculate system cost for any KW size (interpolates for non-standard sizes)
 * @param {number} kwSize - System size in KW
 * @returns {number} Cost before subsidy
 */
export const calculateSystemCost = (kwSize) => {
  // Base costs for standard sizes
  const baseCosts = {
    2: 140000,
    3: 210000,
    4: 270000,
    5: 325000,
    10: 590000
  };
  
  // If exact match, return base cost
  if (baseCosts[kwSize]) {
    return baseCosts[kwSize];
  }
  
  // For sizes between standard sizes, interpolate
  const sizes = Object.keys(baseCosts).map(Number).sort((a, b) => a - b);
  
  // If smaller than minimum, extrapolate down
  if (kwSize < sizes[0]) {
    const costPerKW = baseCosts[sizes[0]] / sizes[0];
    return Math.round(kwSize * costPerKW);
  }
  
  // If larger than maximum, extrapolate up
  if (kwSize > sizes[sizes.length - 1]) {
    // Use average cost per KW from larger systems (slightly decreasing per KW as size increases)
    const avgCostPerKW = 55000; // Average from 5KW and 10KW
    return Math.round(kwSize * avgCostPerKW);
  }
  
  // Interpolate between two standard sizes
  for (let i = 0; i < sizes.length - 1; i++) {
    if (kwSize >= sizes[i] && kwSize <= sizes[i + 1]) {
      const lowerSize = sizes[i];
      const upperSize = sizes[i + 1];
      const lowerCost = baseCosts[lowerSize];
      const upperCost = baseCosts[upperSize];
      
      // Linear interpolation
      const ratio = (kwSize - lowerSize) / (upperSize - lowerSize);
      return Math.round(lowerCost + (upperCost - lowerCost) * ratio);
    }
  }
  
  return 210000; // Default fallback
};

/**
 * Calculate subsidy for any KW size
 * @param {number} kwSize - System size in KW
 * @returns {number} Subsidy amount
 */
export const calculateSubsidy = (kwSize) => {
  if (kwSize >= 3) {
    return 78000; // Maximum subsidy for 3KW and above
  } else if (kwSize >= 2) {
    return 60000; // For 2KW
  } else {
    // For 1KW, proportional subsidy
    return Math.round(30000 * kwSize);
  }
};

/**
 * Find the best system size using mathematical optimization
 * @param {number} monthlyBill - Monthly electricity bill
 * @param {number} electricityRate - Electricity rate per unit
 * @returns {object} Best system recommendation with size and details
 */
export const findOptimalSystemSize = (monthlyBill, electricityRate) => {
  // Calculate required units and KW
  const monthlyUnits = monthlyBill / electricityRate;
  const yearlyUnits = monthlyUnits * 12;
  const requiredKW = yearlyUnits / 1400; // 1400 units per KW per year
  
  // Define evaluation criteria weights
  const weights = {
    emiRatio: 0.4,      // Prefer lower EMI relative to bill
    coverage: 0.3,       // Prefer better coverage of bill
    savings: 0.2,        // Prefer higher savings
    roi: 0.1             // Prefer better return on investment
  };
  
  let bestOption = null;
  let bestScore = -Infinity;
  
  // Generate candidate system sizes
  const candidateSizes = [];
  
  // For small bills (1K-5K), consider 2-5KW
  if (monthlyBill <= 5000) {
    candidateSizes.push(2, 3, 4, 5);
  }
  // For medium bills (5K-15K), consider 3-10KW
  else if (monthlyBill <= 15000) {
    candidateSizes.push(3, 4, 5, 10);
  }
  // For large bills (15K-30K), consider 5-20KW
  else if (monthlyBill <= 30000) {
    candidateSizes.push(5, 10, 15, 20);
  }
  // For very large bills (30K+), consider 10KW and above
  else {
    // Generate sizes in increments
    for (let kw = 10; kw <= Math.min(100, Math.ceil(requiredKW * 1.2)); kw += 5) {
      candidateSizes.push(kw);
    }
  }
  
  // Also include the calculated required size and nearby sizes
  const requiredSizeInt = Math.ceil(requiredKW);
  if (!candidateSizes.includes(requiredSizeInt)) {
    candidateSizes.push(requiredSizeInt);
  }
  if (requiredSizeInt > 1 && !candidateSizes.includes(requiredSizeInt - 1)) {
    candidateSizes.push(requiredSizeInt - 1);
  }
  if (!candidateSizes.includes(requiredSizeInt + 1)) {
    candidateSizes.push(requiredSizeInt + 1);
  }
  
  // Remove duplicates and sort
  const uniqueSizes = [...new Set(candidateSizes)].sort((a, b) => a - b);
  
  // Evaluate each candidate
  for (const kwSize of uniqueSizes) {
    const costBeforeSubsidy = calculateSystemCost(kwSize);
    const subsidy = calculateSubsidy(kwSize);
    const costAfterSubsidy = costBeforeSubsidy - subsidy;
    const loanAmount = costAfterSubsidy - 1; // 1 rupee downpayment
    const emi = calculateEMI(loanAmount, 0.06, 5);
    
    const monthlyGeneration = calculateMonthlyGeneration(kwSize);
    const generatedValue = monthlyGeneration.average * electricityRate;
    
    // Calculate metrics for scoring
    const emiRatio = monthlyBill > 0 ? emi / monthlyBill : Infinity;
    const coverage = Math.min(1, generatedValue / monthlyBill); // How much of bill is covered
    const monthlySavings = monthlyBill - emi; // Immediate savings
    const savingsAfterLoan = monthlyBill; // After loan, full bill is saved
    const totalSavings5Years = savingsAfterLoan * 60; // 5 years of savings after loan
    const netProfit = totalSavings5Years - (emi * 60); // Net profit over 5 years
    const roi = costAfterSubsidy > 0 ? (netProfit / costAfterSubsidy) * 100 : 0;
    
    // Calculate composite score
    let score = 0;
    
    // EMI Ratio Score (lower is better, but we want EMI <= bill ideally)
    if (emiRatio <= 1) {
      score += weights.emiRatio * (1 - emiRatio); // Full points if EMI <= bill
    } else if (emiRatio <= 1.3) {
      score += weights.emiRatio * (1 - (emiRatio - 1) * 0.5); // Partial points
    } else {
      score += weights.emiRatio * 0.1; // Minimal points if EMI > 1.3x bill
    }
    
    // Coverage Score (higher is better, but don't over-generate)
    if (coverage >= 0.8 && coverage <= 1.2) {
      score += weights.coverage * 1; // Ideal coverage (80-120% of bill)
    } else if (coverage >= 0.6 && coverage < 0.8) {
      score += weights.coverage * 0.7; // Good coverage
    } else if (coverage > 1.2 && coverage <= 1.5) {
      score += weights.coverage * 0.8; // Slight over-generation is okay
    } else {
      score += weights.coverage * 0.3; // Too little or too much
    }
    
    // Savings Score (higher is better)
    if (monthlySavings > 0) {
      score += weights.savings * Math.min(1, monthlySavings / monthlyBill);
    } else {
      score += weights.savings * 0.2; // Still some value even if EMI > bill
    }
    
    // ROI Score (higher is better, normalized)
    const normalizedROI = Math.min(1, roi / 200); // Cap at 200% ROI
    score += weights.roi * normalizedROI;
    
    // Bonus: Prefer systems where EMI <= bill
    if (emi <= monthlyBill) {
      score += 0.2; // Bonus points
    }
    
    // Penalty: Avoid systems that generate way too much
    if (generatedValue > monthlyBill * 1.5) {
      score -= 0.1; // Penalty for over-sizing
    }
    
    // Track best option
    if (score > bestScore) {
      bestScore = score;
      bestOption = {
        kwSize,
        emi,
        costBeforeSubsidy,
        subsidy,
        costAfterSubsidy,
        loanAmount,
        monthlyGeneration,
        generatedValue,
        score,
        coverage,
        monthlySavings,
        roi
      };
    }
  }
  
  return bestOption;
};

/**
 * Get system cost before subsidy (legacy function for backward compatibility)
 * @param {number} kwSize - System size in KW
 * @returns {number} Cost before subsidy
 */
export const getSystemCost = (kwSize) => {
  return calculateSystemCost(kwSize);
};

/**
 * Get subsidy amount for given system size (legacy function for backward compatibility)
 * @param {number} kwSize - System size in KW
 * @returns {number} Subsidy amount
 */
export const getSubsidy = (kwSize) => {
  return calculateSubsidy(kwSize);
};

/**
 * Calculate EMI using standard formula
 * @param {number} principal - Loan amount (cost after subsidy - downpayment)
 * @param {number} annualRate - Annual interest rate (as decimal, e.g., 0.06 for 6%)
 * @param {number} tenureYears - Loan tenure in years
 * @returns {number} Monthly EMI amount
 */
export const calculateEMI = (principal, annualRate = 0.06, tenureYears = 5) => {
  if (principal <= 0) return 0;
  
  const monthlyRate = annualRate / 12;
  const numberOfMonths = tenureYears * 12;
  
  // EMI formula: [P × R × (1+R)^N] / [(1+R)^N - 1]
  const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfMonths)) /
              (Math.pow(1 + monthlyRate, numberOfMonths) - 1);
  
  return Math.round(emi);
};

/**
 * Calculate total amount paid over loan tenure
 * @param {number} emi - Monthly EMI amount
 * @param {number} tenureYears - Loan tenure in years
 * @returns {number} Total amount paid
 */
export const calculateTotalLoanAmount = (emi, tenureYears = 5) => {
  return emi * tenureYears * 12;
};

/**
 * Calculate monthly electricity generation for a given system size
 * @param {number} kwSize - System size in KW
 * @returns {object} Object with min, max, and average units per month
 */
export const calculateMonthlyGeneration = (kwSize) => {
  // Base: 3KW generates 340-380 units/month (average 360)
  // Scale proportionally
  const baseMin = 340;
  const baseMax = 380;
  const baseAvg = 360;
  const baseKW = 3;
  
  const minUnits = Math.round((baseMin / baseKW) * kwSize);
  const maxUnits = Math.round((baseMax / baseKW) * kwSize);
  const avgUnits = Math.round((baseAvg / baseKW) * kwSize);
  
  return {
    min: minUnits,
    max: maxUnits,
    average: avgUnits
  };
};

/**
 * Calculate complete solar recommendation based on user inputs
 * @param {object} inputs - User inputs object
 * @param {number} inputs.monthlyBill - Monthly electricity bill
 * @param {number} inputs.electricityRate - Electricity rate per unit
 * @param {string} inputs.systemType - 'on-grid' or 'off-grid'
 * @returns {object} Complete recommendation object
 */
export const calculateSolarRecommendation = (inputs) => {
  const { monthlyBill, electricityRate, systemType } = inputs;
  
  if (!monthlyBill || !electricityRate) {
    return null;
  }
  
  // Find optimal system size where EMI is manageable
  const optimalSystem = findOptimalSystemSize(monthlyBill, electricityRate);
  
  if (!optimalSystem) {
    return null;
  }
  
  const { kwSize, emi, costBeforeSubsidy, subsidy, costAfterSubsidy, loanAmount, monthlyGeneration, generatedValue } = optimalSystem;
  
  // Calculate loan details (1 rupee downpayment)
  const downpayment = 1;
  const totalLoanAmount = calculateTotalLoanAmount(emi, 5);
  
  // Calculate actual savings
  // For on-grid: Generated units offset bill, excess goes to grid
  // Monthly savings = min(bill, generated value) - EMI (if positive)
  const billOffset = Math.min(monthlyBill, generatedValue);
  const netMonthlyCost = emi - billOffset;
  const monthlySavings = monthlyBill - emi; // Actual savings compared to current bill
  
  // Determine if this is a good recommendation
  const isGoodRecommendation = emi <= monthlyBill;
  const savingsAfterLoan = monthlyBill - emi; // After loan period, savings = full bill
  
  return {
    kwSize,
    costBeforeSubsidy,
    subsidy,
    costAfterSubsidy,
    downpayment,
    loanAmount,
    emi,
    totalLoanAmount,
    monthlyGeneration,
    monthlyBill,
    monthlySavings,
    billOffset,
    netMonthlyCost,
    generatedValue,
    isGoodRecommendation,
    savingsAfterLoan,
    systemType
  };
};

/**
 * Format currency in Indian format
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Format number with Indian number system
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (num) => {
  return new Intl.NumberFormat('en-IN').format(num);
};

